import { IEntityInfo, NSARouterMakerResult } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { VolumeRow, VolumeType } from "namirasoft-nws-volume";
import { NamirasoftMap } from "namirasoft-site-map";
import { IStorageLocal } from "namirasoft-core";
import { BaseColumnFormatter, DateTimeFormatter, EnumFormatter, ForeColorFormatter, IDFormatter, JsonFormatter, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { NWSVolumeRouterProps } from "./NWSVolumeRouterProps";
import { NWSVolumeRoutes } from "./NWSVolumeRoutes";

export class NWSVolumeEntities
{
    static volume(props: NSARouterMakerResult & NWSVolumeRouterProps): IEntityInfo<VolumeRow, VolumeRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_NWS_VOLUME,
            name: "volume",
            tables: [{ table: namirasoft.nws_volume.tables.volume, required: false }],
            client: {
                getListURL: () => NWSVolumeRoutes.VolumeList,
                getNewURL: () => NWSVolumeRoutes.VolumeNew,
                getViewURL: (id) => NWSVolumeRoutes.VolumeView(id),
                getCopyURL: (id) => NWSVolumeRoutes.VolumeCopy(id),
                getEditURL: (id) => NWSVolumeRoutes.VolumeEdit(id),
                getColumns: () =>
                {
                    return [
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.workspace_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.region_id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.log_group_id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.type, new StringFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.meta, new JsonFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.status, new ForeColorFormatter(new EnumFormatter(), {
                            InProgress: "gray",
                            Pending: "orange",
                            Ready: "green",
                            Failed: "red"
                        })),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.status_message, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.ui_url, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.nws_volume.tables.volume.columns.updated_at, new DateTimeFormatter()),
                    ];
                },
                onCreate: async (entity) =>
                {
                    let body = { ...entity, ...entity.meta };
                    delete body.id;
                    delete body.user_id;
                    delete body.type;
                    delete body.meta;
                    delete body.status;
                    delete body.status_message;
                    delete body.volume_field;
                    delete body.volume_category;
                    delete body.volume_tag;
                    delete body.created_at;
                    delete body.updated_at;

                    switch (entity.type)
                    {
                        case VolumeType.AWSS3:
                            return await props.servers.nws_volume.aws_s3.Create(body);
                        case VolumeType.AzureBlobStorage:
                            return await props.servers.nws_volume.azure_blob_storage_key.Create(body);
                        case VolumeType.AzureFiles:
                            return await props.servers.nws_volume.azure_files_key.Create(body);
                        case VolumeType.GCPCloudStorage:
                            return await props.servers.nws_volume.gcp_cloud_storage_o_auth2.Create(body);
                        case VolumeType.NamirasoftBucketStorage:
                            return await props.servers.nws_volume.namirasoft_bucket_storage.Create(body);
                        case VolumeType.NamirasoftDiskFixedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_fixed_price.Create(body);
                        case VolumeType.NamirasoftDiskUsageBasedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_usage_based_price.Create(body);
                        case VolumeType.SFTP:
                            return await props.servers.nws_volume.sftp.Create(body);
                        case VolumeType.FTP:
                            return await props.servers.nws_volume.ftp.Create(body);
                        case VolumeType.HTTP:
                            return await props.servers.nws_volume.http.Create(body);
                        case VolumeType.SMB:
                            return await props.servers.nws_volume.smb.Create(body);
                        case VolumeType.WebDAV:
                            return await props.servers.nws_volume.web_dav_user_pass.Create(body);
                        case VolumeType.S3:
                            return await props.servers.nws_volume.s3.Create(body);
                        case VolumeType.GoogleDrive:
                            return await props.servers.nws_volume.google_drive_o_auth.Create(body);
                        case VolumeType.GooglePhotos:
                            return await props.servers.nws_volume.google_photos.Create(body);
                        case VolumeType.BackBlazeB2:
                            return await props.servers.nws_volume.back_blaze_b2.Create(body);
                        case VolumeType.DropboxOAuth:
                            return await props.servers.nws_volume.dropbox_o_auth.Create(body);
                        case VolumeType.OneDriveOAuth:
                            return await props.servers.nws_volume.one_drive_o_auth.Create(body);
                        case VolumeType.Box:
                            return await props.servers.nws_volume.box.Create(body);
                        case VolumeType.Cloudinary:
                            return await props.servers.nws_volume.cloudinary.Create(body);
                        case VolumeType.FileLU:
                            return await props.servers.nws_volume.file_lu.Create(body);
                        case VolumeType.FileFabric:
                            return await props.servers.nws_volume.file_fabric.Create(body);
                        case VolumeType._1Fichier:
                            return await props.servers.nws_volume.fichier.Create(body);
                        case VolumeType.CitrixeShareFile:
                            return await props.servers.nws_volume.citrixe_share_file.Create(body);
                        case VolumeType.GoFile:
                            return await props.servers.nws_volume.go_file.Create(body);
                        case VolumeType.HDFS:
                            return await props.servers.nws_volume.hdfs.Create(body);
                        case VolumeType.HiDrive:
                            return await props.servers.nws_volume.hi_drive.Create(body);
                        case VolumeType.ICloudDrive:
                            return await props.servers.nws_volume.i_cloud_drive.Create(body);
                        case VolumeType.ImageKit:
                            return await props.servers.nws_volume.image_kit.Create(body);
                        case VolumeType.InternetArchive:
                            return await props.servers.nws_volume.internet_archive.Create(body);
                        case VolumeType.Jottacloud:
                            return await props.servers.nws_volume.jottacloud.Create(body);
                        case VolumeType.Koofr:
                            return await props.servers.nws_volume.koofr.Create(body);
                        case VolumeType.Linkbox:
                            return await props.servers.nws_volume.linkbox.Create(body);
                        case VolumeType.Mailru:
                            return await props.servers.nws_volume.mailru.Create(body);
                        case VolumeType.Mega:
                            return await props.servers.nws_volume.mega.Create(body);
                        case VolumeType.OpenDrive:
                            return await props.servers.nws_volume.open_drive.Create(body);
                        case VolumeType.OracleObjectStorage:
                            return await props.servers.nws_volume.oracle_object_storage.Create(body);
                        case VolumeType.PCloud:
                            return await props.servers.nws_volume.p_cloud_o_auth.Create(body);
                        case VolumeType.PikPak:
                            return await props.servers.nws_volume.pik_pak.Create(body);
                        case VolumeType.Pixeldrain:
                            return await props.servers.nws_volume.pixeldrain.Create(body);
                        case VolumeType.PremiumizeMe:
                            return await props.servers.nws_volume.premiumize_me.Create(body);
                        case VolumeType.ProtonDrive:
                            return await props.servers.nws_volume.proton_drive.Create(body);
                        case VolumeType.Putio:
                            return await props.servers.nws_volume.putio.Create(body);
                        case VolumeType.Qingstor:
                            return await props.servers.nws_volume.qingstor.Create(body);
                        case VolumeType.Quatrix:
                            return await props.servers.nws_volume.quatrix.Create(body);
                        case VolumeType.Seafile:
                            return await props.servers.nws_volume.seafile.Create(body);
                        case VolumeType.Sia:
                            return await props.servers.nws_volume.sia.Create(body);
                        case VolumeType.Storj:
                            return await props.servers.nws_volume.storj.Create(body);
                        case VolumeType.Swift:
                            return await props.servers.nws_volume.swift.Create(body);
                        case VolumeType.Ulozto:
                            return await props.servers.nws_volume.ulozto.Create(body);
                        case VolumeType.Yandex:
                            return await props.servers.nws_volume.yandex.Create(body);
                        case VolumeType.Zoho:
                            return await props.servers.nws_volume.zoho.Create(body);
                        case VolumeType.AkamaiNetStorage:
                            return await props.servers.nws_volume.akamai_net_storage.Create(body);
                        default:
                            throw new Error("Wrong volume type");
                    }
                },
                onUpdate: async (id, entity) =>
                {
                    let body = { ...entity, ...entity.meta };
                    delete body.id;
                    delete body.user_id;
                    delete body.type;
                    delete body.meta;
                    delete body.status;
                    delete body.status_message;
                    delete body.volume_field;
                    delete body.volume_category;
                    delete body.volume_tag;
                    delete body.created_at;
                    delete body.updated_at;

                    switch (entity.type)
                    {
                        case VolumeType.AWSS3:
                            return await props.servers.nws_volume.aws_s3.Update(id, body);
                        case VolumeType.AzureBlobStorage:
                            return await props.servers.nws_volume.azure_blob_storage_key.Update(id, body);
                        case VolumeType.AzureFiles:
                            return await props.servers.nws_volume.azure_files_key.Update(id, body);
                        case VolumeType.GCPCloudStorage:
                            return await props.servers.nws_volume.gcp_cloud_storage_o_auth2.Update(id, body);
                        case VolumeType.NamirasoftBucketStorage:
                            return await props.servers.nws_volume.namirasoft_bucket_storage.Update(id, body);
                        case VolumeType.NamirasoftDiskFixedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_fixed_price.Update(id, body);
                        case VolumeType.NamirasoftDiskUsageBasedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_usage_based_price.Update(id, body);
                        case VolumeType.SFTP:
                            return await props.servers.nws_volume.sftp.Update(id, body);
                        case VolumeType.FTP:
                            return await props.servers.nws_volume.ftp.Update(id, body);
                        case VolumeType.HTTP:
                            return await props.servers.nws_volume.http.Update(id, body);
                        case VolumeType.SMB:
                            return await props.servers.nws_volume.smb.Update(id, body);
                        case VolumeType.WebDAV:
                            return await props.servers.nws_volume.web_dav_user_pass.Update(id, body);
                        case VolumeType.S3:
                            return await props.servers.nws_volume.s3.Update(id, body);
                        case VolumeType.GoogleDrive:
                            return await props.servers.nws_volume.google_drive_o_auth.Update(id, body);
                        case VolumeType.GooglePhotos:
                            return await props.servers.nws_volume.google_photos.Update(id, body);
                        case VolumeType.BackBlazeB2:
                            return await props.servers.nws_volume.back_blaze_b2.Update(id, body);
                        case VolumeType.DropboxOAuth:
                            return await props.servers.nws_volume.dropbox_o_auth.Update(id, body);
                        case VolumeType.OneDriveOAuth:
                            return await props.servers.nws_volume.one_drive_o_auth.Update(id, body);
                        case VolumeType.Box:
                            return await props.servers.nws_volume.box.Update(id, body);
                        case VolumeType.Cloudinary:
                            return await props.servers.nws_volume.cloudinary.Update(id, body);
                        case VolumeType.FileLU:
                            return await props.servers.nws_volume.file_lu.Update(id, body);
                        case VolumeType.FileFabric:
                            return await props.servers.nws_volume.file_fabric.Update(id, body);
                        case VolumeType._1Fichier:
                            return await props.servers.nws_volume.fichier.Update(id, body);
                        case VolumeType.CitrixeShareFile:
                            return await props.servers.nws_volume.citrixe_share_file.Update(id, body);
                        case VolumeType.GoFile:
                            return await props.servers.nws_volume.go_file.Update(id, body);
                        case VolumeType.HDFS:
                            return await props.servers.nws_volume.hdfs.Update(id, body);
                        case VolumeType.HiDrive:
                            return await props.servers.nws_volume.hi_drive.Update(id, body);
                        case VolumeType.ICloudDrive:
                            return await props.servers.nws_volume.i_cloud_drive.Update(id, body);
                        case VolumeType.ImageKit:
                            return await props.servers.nws_volume.image_kit.Update(id, body);
                        case VolumeType.InternetArchive:
                            return await props.servers.nws_volume.internet_archive.Update(id, body);
                        case VolumeType.Jottacloud:
                            return await props.servers.nws_volume.jottacloud.Update(id, body);
                        case VolumeType.Koofr:
                            return await props.servers.nws_volume.koofr.Update(id, body);
                        case VolumeType.Linkbox:
                            return await props.servers.nws_volume.linkbox.Update(id, body);
                        case VolumeType.Mailru:
                            return await props.servers.nws_volume.mailru.Update(id, body);
                        case VolumeType.Mega:
                            return await props.servers.nws_volume.mega.Update(id, body);
                        case VolumeType.OpenDrive:
                            return await props.servers.nws_volume.open_drive.Update(id, body);
                        case VolumeType.OracleObjectStorage:
                            return await props.servers.nws_volume.oracle_object_storage.Update(id, body);
                        case VolumeType.PCloud:
                            return await props.servers.nws_volume.p_cloud_o_auth.Update(id, body);
                        case VolumeType.PikPak:
                            return await props.servers.nws_volume.pik_pak.Update(id, body);
                        case VolumeType.Pixeldrain:
                            return await props.servers.nws_volume.pixeldrain.Update(id, body);
                        case VolumeType.PremiumizeMe:
                            return await props.servers.nws_volume.premiumize_me.Update(id, body);
                        case VolumeType.ProtonDrive:
                            return await props.servers.nws_volume.proton_drive.Update(id, body);
                        case VolumeType.Putio:
                            return await props.servers.nws_volume.putio.Update(id, body);
                        case VolumeType.Qingstor:
                            return await props.servers.nws_volume.qingstor.Update(id, body);
                        case VolumeType.Quatrix:
                            return await props.servers.nws_volume.quatrix.Update(id, body);
                        case VolumeType.Seafile:
                            return await props.servers.nws_volume.seafile.Update(id, body);
                        case VolumeType.Sia:
                            return await props.servers.nws_volume.sia.Update(id, body);
                        case VolumeType.Storj:
                            return await props.servers.nws_volume.storj.Update(id, body);
                        case VolumeType.Swift:
                            return await props.servers.nws_volume.swift.Update(id, body);
                        case VolumeType.Ulozto:
                            return await props.servers.nws_volume.ulozto.Update(id, body);
                        case VolumeType.Yandex:
                            return await props.servers.nws_volume.yandex.Update(id, body);
                        case VolumeType.Zoho:
                            return await props.servers.nws_volume.zoho.Update(id, body);
                        case VolumeType.AkamaiNetStorage:
                            return await props.servers.nws_volume.akamai_net_storage.Update(id, body);
                        default:
                            throw new Error("Wrong volume type");
                    }
                },
            },
            server: {
                value_list: props.servers.nws_volume.value.List,
                list: props.servers.nws_volume.volume.List,
                get: props.servers.nws_volume.volume.Get,
                delete: props.servers.nws_volume.volume.Delete,
                create: async (entity) =>
                {
                    let body = {
                        workspace_id: entity.workspace_id,
                        region_id: entity.region_id,
                        log_group_id: entity.log_group_id,
                        name: entity.name,
                        description: entity.description
                    };

                    switch (entity.type)
                    {
                        case VolumeType.AWSS3:
                            return await props.servers.nws_volume.aws_s3.Create({
                                ...body,
                                aws_credential_id: entity.meta.aws_credential_id,
                                provider: entity.meta.provider,
                                region: entity.meta.region,
                            });
                        case VolumeType.AzureBlobStorage:
                            return await props.servers.nws_volume.azure_blob_storage_key.Create({
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                            });
                        case VolumeType.AzureFiles:
                            return await props.servers.nws_volume.azure_files_key.Create({
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                share_name: entity.meta.share_name,
                            });
                        case VolumeType.GCPCloudStorage:
                            return await props.servers.nws_volume.gcp_cloud_storage_o_auth2.Create({
                                ...body,
                                project_number: entity.meta.project_number,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                location: entity.meta.location,
                            });
                        case VolumeType.NamirasoftBucketStorage:
                            return await props.servers.nws_volume.namirasoft_bucket_storage.Create({
                                ...body,
                                provider: entity.meta.provider,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                region: entity.meta.region,
                            });
                        case VolumeType.NamirasoftDiskFixedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_fixed_price.Create({
                                ...body,
                                size: entity.meta.size,
                            });
                        case VolumeType.NamirasoftDiskUsageBasedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_usage_based_price.Create({
                                ...body,
                                size: entity.meta.size,
                            });
                        case VolumeType.SFTP:
                            return await props.servers.nws_volume.sftp.Create({
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                key_pem: entity.meta.key_pem,
                                key_file: entity.meta.key_file,
                                key_file_pass: entity.meta.key_file_pass,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.FTP:
                            return await props.servers.nws_volume.ftp.Create({
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                                tls: entity.meta.tls,
                                explicit_tls: entity.meta.explicit_tls,
                            });
                        case VolumeType.HTTP:
                            return await props.servers.nws_volume.http.Create({
                                ...body,
                                url: entity.meta.url,
                            });
                        case VolumeType.SMB:
                            return await props.servers.nws_volume.smb.Create({
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.WebDAV:
                            return await props.servers.nws_volume.web_dav_user_pass.Create({
                                ...body,
                                url: entity.meta.url,
                                vendor: entity.meta.vendor,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.S3:
                            return await props.servers.nws_volume.s3.Create({
                                ...body,
                                provider: entity.meta.provider,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                region: entity.meta.region,
                            });
                        case VolumeType.GoogleDrive:
                            return await props.servers.nws_volume.google_drive_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.GooglePhotos:
                            return await props.servers.nws_volume.google_photos.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                read_only: entity.meta.read_only,
                            });
                        case VolumeType.BackBlazeB2:
                            return await props.servers.nws_volume.back_blaze_b2.Create({
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                hard_delete: entity.meta.hard_delete,
                            });
                        case VolumeType.DropboxOAuth:
                            return await props.servers.nws_volume.dropbox_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.OneDriveOAuth:
                            return await props.servers.nws_volume.one_drive_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                tenant: entity.meta.tenant,
                            });
                        case VolumeType.Box:
                            return await props.servers.nws_volume.box.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Cloudinary:
                            return await props.servers.nws_volume.cloudinary.Create({
                                ...body,
                                cloud_name: entity.meta.cloud_name,
                                api_key: entity.meta.api_key,
                                api_secret: entity.meta.api_secret,
                            });
                        case VolumeType.FileLU:
                            return await props.servers.nws_volume.file_lu.Create({
                                ...body,
                                key: entity.meta.key,
                            });
                        case VolumeType.FileFabric:
                            return await props.servers.nws_volume.file_fabric.Create({
                                ...body,
                                url: entity.meta.url,
                                root_folder_id: entity.meta.root_folder_id,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType._1Fichier:
                            return await props.servers.nws_volume.fichier.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case VolumeType.CitrixeShareFile:
                            return await props.servers.nws_volume.citrixe_share_file.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.GoFile:
                            return await props.servers.nws_volume.go_file.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.HDFS:
                            return await props.servers.nws_volume.hdfs.Create({
                                ...body,
                                namenode: entity.meta.namenode,
                                username: entity.meta.username,
                            });
                        case VolumeType.HiDrive:
                            return await props.servers.nws_volume.hi_drive.Create({
                                ...body,
                                scope_access: entity.meta.scope_access,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.ICloudDrive:
                            return await props.servers.nws_volume.i_cloud_drive.Create({
                                ...body,
                                apple_id: entity.meta.apple_id,
                                password: entity.meta.password,
                            });
                        case VolumeType.ImageKit:
                            return await props.servers.nws_volume.image_kit.Create({
                                ...body,
                                endpoint: entity.meta.endpoint,
                                public_key: entity.meta.public_key,
                                private_key: entity.meta.private_key,
                            });
                        case VolumeType.InternetArchive:
                            return await props.servers.nws_volume.internet_archive.Create({
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                item_derive: entity.meta.item_derive,
                            });
                        case VolumeType.Jottacloud:
                            return await props.servers.nws_volume.jottacloud.Create({
                                ...body,
                                tokenURL: entity.meta.tokenURL,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Koofr:
                            return await props.servers.nws_volume.koofr.Create({
                                ...body,
                                provider: entity.meta.provider,
                                endpoint: entity.meta.endpoint,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.Linkbox:
                            return await props.servers.nws_volume.linkbox.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Mailru:
                            return await props.servers.nws_volume.mailru.Create({
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.Mega:
                            return await props.servers.nws_volume.mega.Create({
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth: entity.meta.basic_auth,
                            });
                        case VolumeType.OpenDrive:
                            return await props.servers.nws_volume.open_drive.Create({
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.OracleObjectStorage:
                            return await props.servers.nws_volume.oracle_object_storage.Create({
                                ...body,
                                provider: entity.meta.provider,
                                namespace: entity.meta.namespace,
                                compartment: entity.meta.compartment,
                                region: entity.meta.region,
                            });
                        case VolumeType.PCloud:
                            return await props.servers.nws_volume.p_cloud_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.PikPak:
                            return await props.servers.nws_volume.pik_pak.Create({
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.Pixeldrain:
                            return await props.servers.nws_volume.pixeldrain.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case VolumeType.PremiumizeMe:
                            return await props.servers.nws_volume.premiumize_me.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case VolumeType.ProtonDrive:
                            return await props.servers.nws_volume.proton_drive.Create({
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth: entity.meta.basic_auth,
                            });
                        case VolumeType.Putio:
                            return await props.servers.nws_volume.putio.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Qingstor:
                            return await props.servers.nws_volume.qingstor.Create({
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                endpoint: entity.meta.endpoint,
                                zone: entity.meta.zone,
                            });
                        case VolumeType.Quatrix:
                            return await props.servers.nws_volume.quatrix.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                                host: entity.meta.host,
                            });
                        case VolumeType.Seafile:
                            return await props.servers.nws_volume.seafile.Create({
                                ...body,
                                url: entity.meta.url,
                                basic_auth: entity.meta.basic_auth,
                                _2fa: entity.meta._2fa,
                                library: entity.meta.library,
                                library_key: entity.meta.library_key,
                            });
                        case VolumeType.Sia:
                            return await props.servers.nws_volume.sia.Create({
                                ...body,
                                api_url: entity.meta.api_url,
                                api_password: entity.meta.api_password,
                            });
                        case VolumeType.Storj:
                            return await props.servers.nws_volume.storj.Create({
                                ...body,
                                access_grant: entity.meta.access_grant,
                            });
                        case VolumeType.Swift:
                            return await props.servers.nws_volume.swift.Create({
                                ...body,
                                user: entity.meta.user,
                                key: entity.meta.key,
                                auth: entity.meta.auth,
                            });
                        case VolumeType.Ulozto:
                            return await props.servers.nws_volume.ulozto.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                basic_auth: entity.meta.basic_auth,
                            });
                        case VolumeType.Yandex:
                            return await props.servers.nws_volume.yandex.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Zoho:
                            return await props.servers.nws_volume.zoho.Create({
                                ...body,
                                region: entity.meta.region,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.AkamaiNetStorage:
                            return await props.servers.nws_volume.akamai_net_storage.Create({
                                ...body,
                                host: entity.meta.host,
                                account: entity.meta.account,
                                secret: entity.meta.secret,
                            });
                        default:
                            throw new Error("Wrong volume type");
                    }
                },
                update: async (id, entity) =>
                {
                    let body = {
                        workspace_id: entity.workspace_id,
                        region_id: entity.region_id,
                        log_group_id: entity.log_group_id,
                        name: entity.name,
                        description: entity.description
                    };

                    switch (entity.type)
                    {
                        case VolumeType.AWSS3:
                            return await props.servers.nws_volume.aws_s3.Update(id, {
                                ...body,
                                aws_credential_id: entity.meta.aws_credential_id,
                                provider: entity.meta.provider,
                                region: entity.meta.region,
                            });
                        case VolumeType.AzureBlobStorage:
                            return await props.servers.nws_volume.azure_blob_storage_key.Update(id, {
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                            });
                        case VolumeType.AzureFiles:
                            return await props.servers.nws_volume.azure_files_key.Update(id, {
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                share_name: entity.meta.share_name,
                            });
                        case VolumeType.GCPCloudStorage:
                            return await props.servers.nws_volume.gcp_cloud_storage_o_auth2.Update(id, {
                                ...body,
                                project_number: entity.meta.project_number,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                location: entity.meta.location,
                            });
                        case VolumeType.NamirasoftBucketStorage:
                            return await props.servers.nws_volume.namirasoft_bucket_storage.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                region: entity.meta.region,
                            });
                        case VolumeType.NamirasoftDiskFixedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_fixed_price.Update(id, {
                                ...body,
                                size: entity.meta.size,
                            });
                        case VolumeType.NamirasoftDiskUsageBasedPrice:
                            return await props.servers.nws_volume.namirasoft_disk_usage_based_price.Update(id, {
                                ...body,
                                size: entity.meta.size,
                            });
                        case VolumeType.SFTP:
                            return await props.servers.nws_volume.sftp.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                key_pem: entity.meta.key_pem,
                                key_file: entity.meta.key_file,
                                key_file_pass: entity.meta.key_file_pass,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.FTP:
                            return await props.servers.nws_volume.ftp.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                                tls: entity.meta.tls,
                                explicit_tls: entity.meta.explicit_tls,
                            });
                        case VolumeType.HTTP:
                            return await props.servers.nws_volume.http.Update(id, {
                                ...body,
                                url: entity.meta.url,
                            });
                        case VolumeType.SMB:
                            return await props.servers.nws_volume.smb.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.WebDAV:
                            return await props.servers.nws_volume.web_dav_user_pass.Update(id, {
                                ...body,
                                url: entity.meta.url,
                                vendor: entity.meta.vendor,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.S3:
                            return await props.servers.nws_volume.s3.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                region: entity.meta.region,
                            });
                        case VolumeType.GoogleDrive:
                            return await props.servers.nws_volume.google_drive_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.GooglePhotos:
                            return await props.servers.nws_volume.google_photos.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                read_only: entity.meta.read_only,
                            });
                        case VolumeType.BackBlazeB2:
                            return await props.servers.nws_volume.back_blaze_b2.Update(id, {
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                hard_delete: entity.meta.hard_delete,
                            });
                        case VolumeType.DropboxOAuth:
                            return await props.servers.nws_volume.dropbox_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.OneDriveOAuth:
                            return await props.servers.nws_volume.one_drive_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                tenant: entity.meta.tenant,
                            });
                        case VolumeType.Box:
                            return await props.servers.nws_volume.box.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Cloudinary:
                            return await props.servers.nws_volume.cloudinary.Update(id, {
                                ...body,
                                cloud_name: entity.meta.cloud_name,
                                api_key: entity.meta.api_key,
                                api_secret: entity.meta.api_secret,
                            });
                        case VolumeType.FileLU:
                            return await props.servers.nws_volume.file_lu.Update(id, {
                                ...body,
                                key: entity.meta.key,
                            });
                        case VolumeType.FileFabric:
                            return await props.servers.nws_volume.file_fabric.Update(id, {
                                ...body,
                                url: entity.meta.url,
                                root_folder_id: entity.meta.root_folder_id,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType._1Fichier:
                            return await props.servers.nws_volume.fichier.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case VolumeType.CitrixeShareFile:
                            return await props.servers.nws_volume.citrixe_share_file.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.GoFile:
                            return await props.servers.nws_volume.go_file.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.HDFS:
                            return await props.servers.nws_volume.hdfs.Update(id, {
                                ...body,
                                namenode: entity.meta.namenode,
                                username: entity.meta.username,
                            });
                        case VolumeType.HiDrive:
                            return await props.servers.nws_volume.hi_drive.Update(id, {
                                ...body,
                                scope_access: entity.meta.scope_access,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.ICloudDrive:
                            return await props.servers.nws_volume.i_cloud_drive.Update(id, {
                                ...body,
                                apple_id: entity.meta.apple_id,
                                password: entity.meta.password,
                            });
                        case VolumeType.ImageKit:
                            return await props.servers.nws_volume.image_kit.Update(id, {
                                ...body,
                                endpoint: entity.meta.endpoint,
                                public_key: entity.meta.public_key,
                                private_key: entity.meta.private_key,
                            });
                        case VolumeType.InternetArchive:
                            return await props.servers.nws_volume.internet_archive.Update(id, {
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                item_derive: entity.meta.item_derive,
                            });
                        case VolumeType.Jottacloud:
                            return await props.servers.nws_volume.jottacloud.Update(id, {
                                ...body,
                                tokenURL: entity.meta.tokenURL,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Koofr:
                            return await props.servers.nws_volume.koofr.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                endpoint: entity.meta.endpoint,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.Linkbox:
                            return await props.servers.nws_volume.linkbox.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Mailru:
                            return await props.servers.nws_volume.mailru.Update(id, {
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.Mega:
                            return await props.servers.nws_volume.mega.Update(id, {
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth: entity.meta.basic_auth,
                            });
                        case VolumeType.OpenDrive:
                            return await props.servers.nws_volume.open_drive.Update(id, {
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.OracleObjectStorage:
                            return await props.servers.nws_volume.oracle_object_storage.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                namespace: entity.meta.namespace,
                                compartment: entity.meta.compartment,
                                region: entity.meta.region,
                            });
                        case VolumeType.PCloud:
                            return await props.servers.nws_volume.p_cloud_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.PikPak:
                            return await props.servers.nws_volume.pik_pak.Update(id, {
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case VolumeType.Pixeldrain:
                            return await props.servers.nws_volume.pixeldrain.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case VolumeType.PremiumizeMe:
                            return await props.servers.nws_volume.premiumize_me.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case VolumeType.ProtonDrive:
                            return await props.servers.nws_volume.proton_drive.Update(id, {
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth: entity.meta.basic_auth,
                            });
                        case VolumeType.Putio:
                            return await props.servers.nws_volume.putio.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Qingstor:
                            return await props.servers.nws_volume.qingstor.Update(id, {
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                endpoint: entity.meta.endpoint,
                                zone: entity.meta.zone,
                            });
                        case VolumeType.Quatrix:
                            return await props.servers.nws_volume.quatrix.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                                host: entity.meta.host,
                            });
                        case VolumeType.Seafile:
                            return await props.servers.nws_volume.seafile.Update(id, {
                                ...body,
                                url: entity.meta.url,
                                basic_auth: entity.meta.basic_auth,
                                _2fa: entity.meta._2fa,
                                library: entity.meta.library,
                                library_key: entity.meta.library_key,
                            });
                        case VolumeType.Sia:
                            return await props.servers.nws_volume.sia.Update(id, {
                                ...body,
                                api_url: entity.meta.api_url,
                                api_password: entity.meta.api_password,
                            });
                        case VolumeType.Storj:
                            return await props.servers.nws_volume.storj.Update(id, {
                                ...body,
                                access_grant: entity.meta.access_grant,
                            });
                        case VolumeType.Swift:
                            return await props.servers.nws_volume.swift.Update(id, {
                                ...body,
                                user: entity.meta.user,
                                key: entity.meta.key,
                                auth: entity.meta.auth,
                            });
                        case VolumeType.Ulozto:
                            return await props.servers.nws_volume.ulozto.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                basic_auth: entity.meta.basic_auth,
                            });
                        case VolumeType.Yandex:
                            return await props.servers.nws_volume.yandex.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.Zoho:
                            return await props.servers.nws_volume.zoho.Update(id, {
                                ...body,
                                region: entity.meta.region,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case VolumeType.AkamaiNetStorage:
                            return await props.servers.nws_volume.akamai_net_storage.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                account: entity.meta.account,
                                secret: entity.meta.secret,
                            });
                        default:
                            throw new Error("Wrong volume type");
                    }
                }
            }
        };
    };
}